import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { humanError } from "@/lib/api";

export function SuggestionsPanel({ network, brief, onApply }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [voice, setVoice] = useState("");

  async function run() {
    if (!brief.trim()) return toast.error("Add a brief (topic, tone, key points) to get suggestions");
    setBusy(true);
    try {
      const { data } = await api.post("/suggestions", { network, brief, brand_voice: voice });
      setResult(data);
    } catch (e) {
      toast.error(humanError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="suggestions-panel" className="rounded-xl border border-[#E4E9EF] bg-white p-5">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#EEF3FB] text-[#1F5FD8]"><Sparkles className="h-4 w-4" /></span>
          <div>
            <div className="text-sm font-semibold text-[#0B2B33]">Suggestions</div>
            <div className="text-xs text-[#8A97A0]">Caption, hashtags, and CTA tuned for {network}</div>
          </div>
        </div>
        <button
          onClick={run}
          disabled={busy}
          data-testid="suggestions-run-button"
          className="hs-btn-primary text-sm disabled:opacity-60"
        >
          {busy ? (<span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Generating…</span>) : "Suggest"}
        </button>
      </div>
      <div className="mb-3">
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Brand voice (optional)</label>
        <input
          value={voice} onChange={(e) => setVoice(e.target.value)}
          data-testid="brand-voice-input"
          placeholder="e.g. clear, warm, no hype"
          className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm"
        />
      </div>
      {result ? (
        <div className="space-y-3">
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Caption</div>
            <div className="whitespace-pre-wrap rounded-lg border border-[#E4E9EF] bg-[#F6F8FA] p-3 text-sm text-[#0B2B33]">{result.caption}</div>
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Hashtags</div>
            <div className="flex flex-wrap gap-1">
              {(result.hashtags || []).map((h) => <span key={h} className="hs-chip">#{h}</span>)}
              {(!result.hashtags || !result.hashtags.length) && <span className="text-xs text-[#8A97A0]">None</span>}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">CTA</div>
            <div className="rounded-lg border border-[#E4E9EF] bg-[#F6F8FA] p-3 text-sm text-[#0B2B33]">{result.cta || "—"}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="apply-all-button" onClick={() => onApply?.(result)} className="hs-btn-primary text-sm">Apply to this variant</button>
            <button data-testid="apply-hashtags-button" onClick={() => onApply?.({ ...result, caption: undefined, cta: undefined })} className="rounded-full border border-[#E4E9EF] px-4 py-2 text-sm font-semibold text-[#0B2B33] hover:bg-[#F6F8FA]">Just hashtags</button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-[#8A97A0]">Suggestions are a starting point — always review before you publish.</p>
      )}
    </div>
  );
}
